import React, { useEffect, useState } from 'react';

// Hooks
import { useDebounce } from '../../hooks/useDebounce';

// Store
import { Cycle } from '../../store/models/cycles';

// Types
import { SearchCycle } from '../../types/search-name';

// Components
import Modal from '../Modal/Modal';
import InputControl from '../InputControl/InputControl';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';
import { generatePath } from 'react-router-dom';
import ROUTE from '../../constants/route-constants';
import CycleStatus from '../../enums/cycle-status.enum';
import { SEARCH_TEXT_LENGTH_LIMITS } from '../../constants/common';

type ComponentProps = {
  contractId: string;
  visibility: boolean;
  onClose: () => void;
  getCyclesByContractId: ((payload: {
    contractId: string;
    query?: SearchCycle;
  }) => Promise<Cycle[]>) & {
    isEffect: true;
  };
  cyclesBySearch: Cycle[];
  addNewCycle: (id: string) => Promise<void>;
  screenId: string;
  clientId: string;
};

const CycleSearchScreen: React.FC<ComponentProps> = ({
  visibility,
  onClose,
  contractId,
  getCyclesByContractId,
  cyclesBySearch,
  addNewCycle,
  screenId,
  clientId,
}) => {
  const [allCycles, setAllCycles] = useState<Cycle[] | null>(null);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);

  const debouncedSearchQuery = useDebounce<string>(searchQuery, 300);

  const updateSearchText = (event) => {
    const { value } = event.currentTarget;
    setSearchQuery(value);
  };

  const addCycle = async (cycle: Cycle) => {
    await addNewCycle(cycle.id);
    await fetchAll();
  };

  const fetchAll = async () => {
    const response = await getCyclesByContractId({ contractId, query: { status: CycleStatus.Active } });
    setAllCycles(response);
  };

  const searchCyclesByName = async (name: string) => {
    const query: SearchCycle = { name, status: CycleStatus.Active };
    await getCyclesByContractId({ contractId, query });
  };

  const markUsedCycles = (listCycles: Cycle[], currentScreenId: string) => {
    return listCycles.map(cycle => {
      let is_used: boolean = false;

      const isAttachedScreen = cycle.screens.find((cycleScreen) => cycleScreen.id === currentScreenId);

      if (isAttachedScreen) {
        is_used = true;
      }

      return {
        ...cycle,
        is_used,
      }
    });
  };

  const getListCycles = (query: string) => {
    if (query && query.length >= SEARCH_TEXT_LENGTH_LIMITS.MIN) {
      const listCycles = markUsedCycles(cyclesBySearch, screenId);
      return listCycles;
    };

    const listCycles = markUsedCycles(allCycles, screenId);
    return listCycles;
  }

  useEffect(() => {
    if (debouncedSearchQuery && debouncedSearchQuery?.length >= SEARCH_TEXT_LENGTH_LIMITS.MIN) {
      searchCyclesByName(debouncedSearchQuery);
    } else {
      fetchAll();
    }
  }, [debouncedSearchQuery]);

  return (
    <Modal
      isVisible={visibility}
      onClose={onClose}
      modalTitle='Choose cycles'
      showAcceptBtn={false}
      showCancelBtn={false}
    >
      <div className="c-cycle-search-screen h-full w-full">
        {allCycles === null && (
          <div className='h-75 w-full flex justify-center items-center'><SpinnerLoader /></div>
        )}
        {allCycles !== null && (
          <div className='h-80 flex flex-col'>
            <div className='pb-3'>
              <InputControl
                type="text"
                name="email"
                placeholder="Enter cycle name"
                value={searchQuery || ''}
                onChange={updateSearchText}
                autoComplete="off"
              />
            </div>
            <div className='c-cycle-search-screen__wrapper-list overflow-y-auto flex-auto mb-7'>
              {cyclesBySearch.length !== 0 && (
                <ul className="c-cycle-search-screen__list flex-auto flex flex-col gap-y-2 overflow-y-auto overflow-x-hidden">
                  {getListCycles(searchQuery).map((cycle) => (
                    <li key={cycle.id} className='grid gap-x-2 w-full items-center grid-cols-[1fr,auto]'>
                      <span className='truncate text-left'>{cycle.name}</span>
                      <InteractiveButton
                        disabled={cycle.is_used}
                        text={cycle.is_used ? 'Used' : 'Add to screen'}
                        onClick={() => addCycle(cycle)}
                        className='w-31'
                      />
                    </li>
                  ))}
                </ul>
              )}
              {cyclesBySearch.length === 0 && searchQuery && searchQuery.length > 2 && (
                <div className='flex-auto flex justify-center items-center'>
                  <span>No cycles found</span>
                </div>
              )}
            </div>
            <InteractiveButton
              text='New cycle'
              href={generatePath(ROUTE.ADD_SCREEN_CYCLE, { clientId, contractId, screenId })}
              target='_blank'
              as='a'
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CycleSearchScreen;
